import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { showToast } from "../../../core/utils/alert";
import { useDispatch } from "react-redux";
import { logout } from "../../../core/data/slice/authSlice";
import { useLogoutMutation } from "../../auth/login/api/loginApi";

type PasswordField = "password" | "confirmPassword";

const LockScreen = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const [logoutMutation] = useLogoutMutation();
  const [username, setUsername] = useState<string>("User");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const maxAttempts = 3;

  // Use sessionStorage directly for attempt count to avoid state sync issues
  const getAttemptCount = (): number => {
    return parseInt(sessionStorage.getItem("lockScreenAttemptCount") || "0");
  };

  const setAttemptCount = (count: number): void => {
    sessionStorage.setItem("lockScreenAttemptCount", count.toString());
  };

  // Track if max attempts has been reached
  const isMaxAttemptsReached = (): boolean => {
    return (
      getAttemptCount() >= maxAttempts ||
      sessionStorage.getItem("lockScreenMaxAttemptsReached") === "true"
    );
  };

  // Check for both username and lock status on component mount
  useEffect(() => {
    const storedUsername = sessionStorage.getItem("lockScreenUsername");
    const isScreenLocked = sessionStorage.getItem("isScreenLocked");

    // If screen is not locked or no username found, redirect to appropriate page
    if (!isScreenLocked || !storedUsername) {
      // Redirect to dashboard if we have a username (they're probably logged in)
      // otherwise redirect to login
      navigation(storedUsername ? routes.adminDashboard : routes.login);
      return;
    }

    setUsername(storedUsername);

    // Check if max attempts reached and update error message accordingly
    if (isMaxAttemptsReached()) {
      setError("Maximum attempts reached. Please log in again.");
    }

    // Prevent navigation using the history API
    const preventNavigation = (event: PopStateEvent) => {
      event.preventDefault();
      window.history.pushState(null, "", window.location.pathname);
    };

    // Add event listener to prevent back button navigation
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", preventNavigation);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", preventNavigation);
    };
  }, [navigation, routes.adminDashboard, routes.login]);

  // Reset shake animation after it completes
  useEffect(() => {
    if (shake) {
      const timer = setTimeout(() => {
        setShake(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  const navigationPath = () => {
    setLoading(true);
    setError("");

    // Get stored password hash from session storage
    const storedPasswordHash = sessionStorage.getItem("lockScreenPasswordHash");

    if (!storedPasswordHash) {
      // If no password hash found, redirect to login for security
      navigation(routes.login);
      return;
    }

    // Compare entered password with stored hash
    const enteredPasswordHash = btoa(password);

    setTimeout(() => {
      if (enteredPasswordHash === storedPasswordHash) {
        // Password matches, unlock and redirect to dashboard
        // Animate the unlock icon first
        setUnlocking(true);

        // Then proceed with unlocking after animation
        setTimeout(() => {
          // Clear the lock status and attempt count
          sessionStorage.removeItem("isScreenLocked");
          sessionStorage.removeItem("lockScreenAttemptCount");
          sessionStorage.removeItem("lockScreenMaxAttemptsReached");
          navigation(routes.adminDashboard);
          showToast({
            title: "Success",
            text: "Welcome back!",
            icon: "success",
            timer: 3000,
          });
        }, 800); // Wait for animation to complete
      } else {
        // Password doesn't match - trigger shake animation
        setShake(true);

        const currentAttempts = getAttemptCount();
        const newAttemptCount = currentAttempts + 1;
        setAttemptCount(newAttemptCount);

        const attemptsRemaining = maxAttempts - newAttemptCount;

        if (attemptsRemaining > 0) {
          setError(
            `Incorrect password. ${attemptsRemaining} attempt${
              attemptsRemaining > 1 ? "s" : ""
            } remaining.`
          );
        } else {
          setError("Maximum attempts reached. Please log in again.");
          // Don't remove lock status - only the login button will do that
          // Store max attempts reached flag
          sessionStorage.setItem("lockScreenMaxAttemptsReached", "true");
        }

        setLoading(false);
        showToast({
          title: "Error",
          text: `Incorrect password${
            attemptsRemaining > 0
              ? `. ${attemptsRemaining} attempt${
                  attemptsRemaining > 1 ? "s" : ""
                } remaining`
              : ""
          }`,
          icon: "error",
          timer: 3000,
        });
      }
    }, 800);
  };

  // Handle login redirection and perform proper logout
  const handleLoginRedirect = async () => {
    try {
      setLoginLoading(true);
      // Perform API logout
      await logoutMutation().unwrap();

      // Dispatch Redux logout action
      dispatch(logout());

      // Clear lock screen related session storage
      sessionStorage.removeItem("isScreenLocked");
      sessionStorage.removeItem("lockScreenMaxAttemptsReached");
      sessionStorage.removeItem("lockScreenAttemptCount");
      sessionStorage.removeItem("lockScreenUsername");
      sessionStorage.removeItem("lockScreenPasswordHash");

      // Navigate to login
      navigation(routes.login);

      showToast({
        title: "Success",
        text: "You have been logged out successfully",
        icon: "success",
        timer: 3000,
      });
    } catch (error) {
      console.error("Logout failed:", error);

      // Even if API fails, we should still clear local state and redirect
      dispatch(logout());
      sessionStorage.removeItem("isScreenLocked");
      sessionStorage.removeItem("lockScreenMaxAttemptsReached");
      sessionStorage.removeItem("lockScreenAttemptCount");
      sessionStorage.removeItem("lockScreenUsername");
      sessionStorage.removeItem("lockScreenPasswordHash");
      navigation(routes.login);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigationPath();
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // Calculate warning level for remaining attempts
  const getAttemptWarningLevel = () => {
    const attemptsRemaining = maxAttempts - getAttemptCount();
    if (attemptsRemaining === 0) return "danger";
    if (attemptsRemaining === 1) return "warning";
    return "info";
  };

  // Reset wrong password animation after it completes
  useEffect(() => {
    if (shake) {
      const timer = setTimeout(() => {
        setShake(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  return (
    <>
      <style>
        {`
          body, html {
            overflow-x: hidden;
          }
          .lock-container {
            min-height: 100vh;
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            padding: 1rem 0.5rem 0.5rem;
          }
          .logo-area {
            text-align: center;
            width: 100%;
          }
          .logo-image {
            max-height: 120px;
          }
          .card-wrapper {
            width: 100%;
          }
          .card-area {
            width: 100%;
            max-width: 450px;
            margin: 0 auto;
          }
          .footer-area {
            margin-top: auto;
            text-align: center;
            padding: 1rem 0;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            15% { transform: translateX(-6px) rotate(-1deg); }
            30% { transform: translateX(6px) rotate(1deg); }
            45% { transform: translateX(-6px) rotate(-1deg); }
            60% { transform: translateX(6px) rotate(1deg); }
            75% { transform: translateX(-6px) rotate(-1deg); }
            90% { transform: translateX(6px) rotate(1deg); }
            100% { transform: translateX(0); }
          }
          .shake-animation {
            animation: shake 0.5s ease-in-out;
          }
          .lock-icon-container {
            position: relative;
            transition: all 0.3s ease;
          }
          .lock-icon-container:hover {
            transform: scale(1.05);
          }
          .password-input-wrapper {
            transition: all 0.3s ease;
          }
          .password-input-wrapper:focus-within {
            transform: translateY(-2px);
            box-shadow: 0 6px 12px rgba(0,0,0,0.1);
          }
          .unlock-button {
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;
          }
          .unlock-button:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
          .unlock-button::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              to right,
              rgba(255,255,255,0) 0%,
              rgba(255,255,255,0.3) 50%,
              rgba(255,255,255,0) 100%
            );
            transition: all 0.6s;
          }
          .unlock-button:hover:not(:disabled)::after {
            left: 100%;
          }
          .attempts-badge {
            border-radius: 12px;
            font-size: 12px;
            padding: 3px 8px;
            margin-left: 8px;
            font-weight: 600;
          }
          @keyframes unlockAnimation {
            0% { transform: rotate(0); }
            10% { transform: rotate(-8deg); }
            20% { transform: rotate(0deg); }
            30% { transform: rotate(8deg) translateY(0); }
            40% { transform: rotate(0deg) translateY(-2px); }
            50% { transform: rotate(0deg) translateY(-4px); }
            60% { transform: rotate(0deg) translateY(-6px); }
            75% { transform: rotate(0deg) translateY(-8px); }
            100% { transform: rotate(0deg) translateY(-10px) scale(1.05); }
          }
          
          .icon-unlock {
            animation: unlockAnimation 0.8s ease-out forwards;
            transform-origin: center bottom;
          }
          
          .lock-icon-container::after {
            content: '';
            position: absolute;
            width: 60%;
            height: 6px;
            background-color: rgba(0,0,0,0.15);
            bottom: -8px;
            left: 20%;
            border-radius: 50%;
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          
          .unlock-shadow::after {
            opacity: 1;
            animation: shadowExpand 0.8s ease-out forwards;
          }
          
          @keyframes shadowExpand {
            0% { transform: scale(0.5); opacity: 0; }
            50% { opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          @keyframes wrongPasswordEffect {
            0% { 
              transform: translateX(0) rotate(0); 
            }
            10% { 
              transform: translateX(-5px) rotate(-3deg); 
            }
            30% { 
              transform: translateX(5px) rotate(3deg); 
            }
            50% { 
              transform: translateX(-5px) rotate(-3deg); 
            }
            70% { 
              transform: translateX(5px) rotate(3deg); 
            }
            90% { 
              transform: translateX(-5px) rotate(-3deg); 
            }
            100% { 
              transform: translateX(0) rotate(0); 
            }
          }
          
          .wrong-password-animation {
            animation: wrongPasswordEffect 0.6s ease-in-out;
          }
          
          .wrong-password-icon {
            transition: color 0.6s ease-out;
          }
        `}
      </style>

      <div className="lock-container">
        {/* Logo Area */}
        <div className="logo-area">
          <ImageWithBasePath
            src="assets/img/authentication/authentication-logo.png"
            className="logo-image"
            alt="Logo"
          />
        </div>

        {/* Card Area */}
        <div className="card-wrapper">
          <div className="card-area">
            <form onSubmit={handleSubmit}>
              <div
                className="card"
                style={{ border: "none", boxShadow: "none" }}
              >
                <div className="card-body p-3">
                  <div className="mb-3 text-center">
                    <h2 className="mb-1">Welcome back!</h2>
                    <small className="text-muted d-block mb-2">
                      Please use your login password to unlock the screen
                    </small>
                    <div className="d-flex justify-content-center mb-2">
                      <div
                        className={`lock-icon-container ${
                          unlocking ? "unlock-shadow" : ""
                        } ${shake ? "wrong-password-animation" : ""}`}
                      >
                        <div
                          className="avatar avatar-xxl rounded-circle bg-light-primary d-flex align-items-center justify-content-center"
                          style={{
                            width: "110px",
                            height: "110px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <i
                            className={`ti ${
                              unlocking
                                ? "ti-lock-open icon-unlock text-success"
                                : "ti-lock"
                            } ${
                              shake
                                ? "wrong-password-icon text-danger"
                                : "text-primary"
                            }`}
                            style={{
                              fontSize: "3.5rem",
                              transition: "all 0.3s ease",
                            }}
                          ></i>
                        </div>
                      </div>
                    </div>
                    <h5 className="text-dark mb-0 mt-2">{username}</h5>
                    {!isMaxAttemptsReached() && (
                      <div className="d-flex align-items-center justify-content-center mt-2">
                        <small className="text-muted">
                          Remaining attempts:
                        </small>
                        <span
                          className={`attempts-badge bg-${getAttemptWarningLevel()}-subtle text-${getAttemptWarningLevel()}`}
                        >
                          {maxAttempts - getAttemptCount()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mb-1">
                    <div
                      className={`password-input-wrapper pass-group position-relative rounded overflow-hidden ${
                        shake ? "shake-animation" : ""
                      } ${isMaxAttemptsReached() ? "opacity-50" : ""}`}
                    >
                      <input
                        type={passwordVisibility.password ? "text" : "password"}
                        className={`pass-input form-control border-0 py-2 ${
                          error ? "is-invalid" : ""
                        }`}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoFocus
                        disabled={isMaxAttemptsReached()}
                        aria-label="Password"
                      />
                      <span
                        className={`ti toggle-passwords ${
                          passwordVisibility.password ? "ti-eye" : "ti-eye-off"
                        }`}
                        onClick={() =>
                          !isMaxAttemptsReached() &&
                          togglePasswordVisibility("password")
                        }
                        style={{
                          position: "absolute",
                          right: "20px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: isMaxAttemptsReached()
                            ? "not-allowed"
                            : "pointer",
                          zIndex: 5,
                          opacity: isMaxAttemptsReached() ? 0.5 : 1,
                        }}
                        aria-label={
                          passwordVisibility.password
                            ? "Hide password"
                            : "Show password"
                        }
                        role="button"
                        tabIndex={0}
                      ></span>
                    </div>
                    <div
                      className="error-space"
                      style={{ minHeight: "16px", margin: "0.1rem 0" }}
                    >
                      {error && (
                        <div
                          className={`text-${
                            isMaxAttemptsReached()
                              ? "danger fw-semibold"
                              : "danger"
                          } small d-flex align-items-center`}
                        >
                          {isMaxAttemptsReached() && (
                            <i className="ti ti-alert-triangle me-1"></i>
                          )}
                          {error}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100 py-2 unlock-button shadow-sm mt-1"
                    disabled={loading || isMaxAttemptsReached()}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Unlocking...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-lock-open me-2"></i>
                        Unlock
                      </>
                    )}
                  </button>

                  {isMaxAttemptsReached() && (
                    <div className="mt-3 pt-2 border-top">
                      <div className="text-center mb-2">
                        <div className="d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger rounded-pill px-3 py-1 mb-1">
                          <i className="ti ti-shield-lock me-2"></i>
                          <span>Account Security Measure</span>
                        </div>
                        <p className="text-muted small mb-0">
                          For your security, this screen has been locked due to
                          multiple failed attempts.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 py-2 mt-1"
                        onClick={handleLoginRedirect}
                        disabled={loginLoading}
                      >
                        {loginLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Logging out...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-login me-2"></i>
                            Proceed to Login
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Area */}
        <div className="footer-area">
          <p className="mb-0 text-muted small">
            Copyright © {new Date().getFullYear()} - AlohaJK
          </p>
        </div>
      </div>
    </>
  );
};

export default LockScreen;
